/**
 * A work item's finding in the operator's language. The server writes each item's `summary` as a
 * technical English line for support ("VNNIC_REJECTED: R01", "DOMAIN_REGISTRATION FAILED:
 * REGISTRY_REJECTED:2302 @compensate", "domain_create UNKNOWN (x.test)"); its `detail` carries the
 * same facts as data. The sentence is built from `kind` and `detail` (strings in the `work`
 * namespace, `summary.*`); the server line stays available as a technical detail. A kind without a
 * sentence here keeps the server line when it is already Vietnamese, else shows none.
 *
 * Plain functions with a lookup so tests run in Node (tests/work-summary.test.mjs).
 */

export interface WorkSummaryItem {
  kind: string;
  summary: string;
  detail?: Record<string, unknown> | null;
}

export interface WorkSummaryText {
  /** The sentence to show; null when there is none in the operator's language. */
  text: string | null;
  /** The server's line, for "Chi tiết kỹ thuật"; null when it is the text itself. */
  detail: string | null;
}

/** `summary.<key>` in the `work` namespace (null when missing). */
export type SummaryLookup = (key: string) => string | undefined;

const VI = /[àáảãạăằắẳẵặâầấẩẫậèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵđ]/i;

const fill = (template: string, values: Record<string, string>) => template.replace(/\{\{(\w+)\}\}/g, (_, k: string) => values[k] ?? '');

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : null);

export function workSummaryWith(item: WorkSummaryItem, lookup: SummaryLookup, vietnamese: boolean): WorkSummaryText {
  const d = item.detail ?? {};
  const s = (key: string) => lookup(`summary.${key}`);
  /** A vocabulary entry, else the code itself in readable form. */
  const word = (group: string, code: string | null) => (code ? (s(`${group}.${code}`) ?? code.toLowerCase().replace(/_/g, ' ')) : '');
  const code = (c: string | null) => (c ? fill(s('code') ?? ' ({{code}})', { code: c }) : '');
  let text: string | undefined;
  switch (item.kind) {
    case 'VNNIC_SYNC_REJECTED': {
      const status = str(d.sync_status) ?? 'REJECTED';
      text = s(`vnnicRejected.${status}`) ?? s('vnnicRejected.REJECTED');
      if (text) text = fill(text, { code: code(str(d.last_error_code)) });
      break;
    }
    case 'VNNIC_SYNC_MISSING':
    case 'VNNIC_SYNC_DRIFT':
    case 'DOSSIER_MANUAL_REVIEW':
      text = s(item.kind);
      break;
    case 'VNNIC_SYNC_STUCK': {
      const t = s(item.kind);
      if (t) text = fill(t, { status: word('syncStatus', str(d.sync_status)), code: code(str(d.last_error_code)) });
      break;
    }
    case 'WORKFLOW_FAILED':
    case 'WORKFLOW_MANUAL_REVIEW': {
      const t = s(item.kind);
      if (t) text = fill(t, { workflow: word('workflow', str(d.workflow_type)), step: word('step', str(d.step)), code: code(str(d.failure_code)) });
      break;
    }
    case 'REGISTRY_TRANSACTION': {
      const grouped = d.grouped === true;
      const t = s(grouped ? 'registryTransactionGrouped' : 'registryTransaction');
      if (t) text = fill(t, { operation: word('operation', str(d.operation)), count: str(d.count) ?? '', registry: str(d.registry_code) ?? '' });
      break;
    }
    case 'RECONCILIATION_ISSUE': {
      const type = str(d.issue_type);
      text = (type ? s(`reconciliation.${type}`) : undefined) ?? (s('reconciliation.other') ? fill(s('reconciliation.other') as string, { type: word('issue', type) }) : undefined);
      break;
    }
    case 'COVERAGE_GAP': {
      const t = s(item.kind);
      if (t) text = fill(t, { gap: word('gap', str(d.gap)) });
      break;
    }
    case 'VNNIC_LIST_MISMATCH': {
      const t = s(item.kind);
      if (t) text = fill(t, { mismatch: word('mismatch', str(d.mismatch)) });
      break;
    }
    case 'NOTIFICATION_FAILED': {
      const t = s(item.kind);
      if (t) text = fill(t, { template: str(d.template_key) ?? '', attempts: str(d.attempts) ?? '?' });
      break;
    }
    case 'VNNIC_PANEL_REGISTRY_LOCK':
    case 'VNNIC_PANEL_REGISTRY_UNLOCK':
      text = s(item.kind);
      break;
    default:
      break;
  }
  if (text) return { text, detail: item.summary && item.summary !== text ? item.summary : null };
  // No sentence for this kind: the server's line when it reads in the operator's language.
  if (!vietnamese || VI.test(item.summary)) return { text: item.summary || null, detail: null };
  return { text: null, detail: item.summary || null };
}
